/**
 * SectionSkeletons.tsx
 *
 * Shared skeleton renderers for UnifiedMediaSection variants.
 */

import React from "react";
import { DynamicSkeletonLoader, Skeleton } from "../../SkeletonLoader/SkeletonLoader";

/**
 * Shared skeleton renderers used by section modules.
 */

/**
 * Horizontal card skeleton used by live/previous list sections.
 */
export function HorizontalCardSkeleton({
  count = 5,
  title = "",
}: Readonly<{ count?: number; title?: string }>) {
  return (
    <DynamicSkeletonLoader
      className="mt-4 flex [scrollbar-width:none] gap-4 overflow-x-auto [&::-webkit-scrollbar]:hidden"
      aria-busy="true"
      aria-label={`Loading ${title} videos`}
    >
      <span className="sr-only">Loading {title} videos, please wait...</span>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={`hsk-${title}-${i}`}
          className="min-w-[110px] shrink-0 sm:min-w-[140px] md:min-w-[190px] lg:min-w-[200px] xl:min-w-[210px]"
        >
          <Skeleton
            variant="rectangular"
            className="mb-3 aspect-[9/15.8] w-full rounded-lg bg-gray-200 sm:aspect-[9/16.3]"
            aria-hidden="true"
          />
          <Skeleton
            variant="text"
            className="mb-1 h-[12px] w-[80%] bg-gray-200"
            aria-hidden="true"
          />
          <Skeleton variant="text" className="h-[12px] w-[55%] bg-gray-200" aria-hidden="true" />
        </div>
      ))}
    </DynamicSkeletonLoader>
  );
}

/**
 * Wide card skeleton used by playlist/upcoming sections.
 */
export function WideCardSkeleton({
  count = 3,
  title = "",
}: Readonly<{ count?: number; title?: string }>) {
  return (
    <DynamicSkeletonLoader className="mt-3" aria-busy="true" aria-label={`Loading ${title}`}>
      <span className="sr-only">Loading {title}, please wait...</span>
      <div className="hidden gap-4 overflow-x-auto sm:flex">
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={`wdk-${title}-${i}`}
            className="min-w-[300px] shrink-0 sm:min-w-[280px] md:min-w-[310px] lg:min-w-[320px]"
          >
            <Skeleton
              variant="rectangular"
              className="mb-2 h-[190px] w-full rounded-lg bg-gray-200"
              aria-hidden="true"
            />
            <Skeleton
              variant="text"
              className="mb-1 h-[12px] w-[70%] bg-gray-200"
              aria-hidden="true"
            />
            <Skeleton variant="text" className="h-[12px] w-[45%] bg-gray-200" aria-hidden="true" />
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-3 sm:hidden">
        {Array.from({ length: Math.min(count, 2) }).map((_, i) => (
          <div key={`wmk-${title}-${i}`}>
            <Skeleton
              variant="rectangular"
              className="mb-2 h-[180px] w-full rounded-lg bg-gray-200"
              aria-hidden="true"
            />
            <Skeleton
              variant="text"
              className="mb-1 h-[12px] w-[70%] bg-gray-200"
              aria-hidden="true"
            />
          </div>
        ))}
      </div>
    </DynamicSkeletonLoader>
  );
}

/**
 * Tab + card skeleton used by category section.
 */
export function CategorySkeleton({ title = "" }: Readonly<{ title?: string }>) {
  return (
    <DynamicSkeletonLoader className="mt-3" aria-busy="true" aria-label={`Loading ${title}`}>
      <span className="sr-only">Loading {title}, please wait...</span>
      <div className="mb-4 flex gap-3 border-b border-gray-200 pb-2">
        {["tab-a", "tab-b", "tab-c", "tab-d"].map((tabKey) => (
          <Skeleton
            key={tabKey}
            variant="text"
            className="h-[28px] w-[70px] rounded-md bg-gray-200"
            aria-hidden="true"
          />
        ))}
      </div>
      <div className="flex [scrollbar-width:none] gap-4 overflow-x-auto [&::-webkit-scrollbar]:hidden">
        {["card-a", "card-b", "card-c", "card-d", "card-e"].map((cardKey) => (
          <div
            key={cardKey}
            className="flex-[0_0_210px] shrink-0 max-[1200px]:flex-[0_0_180px] max-[768px]:flex-[0_0_120px] max-[420px]:flex-[0_0_109px]"
          >
            <Skeleton
              variant="rectangular"
              className="mb-2 aspect-[9/16.3] w-full rounded-lg bg-gray-200"
              aria-hidden="true"
            />
            <Skeleton
              variant="text"
              className="mb-1 h-[12px] w-[75%] bg-gray-200"
              aria-hidden="true"
            />
          </div>
        ))}
      </div>
    </DynamicSkeletonLoader>
  );
}
